// src/pages/client/PackageDetailsPage.jsx
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarMonthIcon,
  FlightTakeoff as FlightTakeoffIcon,
  FlightLand as FlightLandIcon,
  Hotel as HotelIcon,
  LocationOn as LocationOnIcon,
  AttachMoney as DollarIcon,
  EventAvailable as EventAvailableIcon,
  Share as ShareIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Star as StarIcon,
} from '@mui/icons-material';

import { fetchPackageById, resetCurrentPackage } from '../../features/packages/packagesSlice';

const PackageDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentPackage, isLoading, error } = useSelector((state) => state.packages);

  useEffect(() => {
    dispatch(fetchPackageById(id));

    // Cleanup on unmount
    return () => {
      dispatch(resetCurrentPackage());
    };
  }, [dispatch, id]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: currentPackage?.title,
        text: `Découvrez ce forfait Hajj: ${currentPackage?.title}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // Ici on pourrait ajouter une notification pour indiquer que l'URL a été copiée
    }
  };

  // Constantes pour les choix de gamme et leurs couleurs
  const categoryColors = {
    standard_non_shifting: '#4caf50', // vert
    standard_shifting: '#8bc34a', // vert clair
    premium_non_shifting: '#ff9800', // orange
    premium_shifting: '#ffc107', // jaune
    luxury_non_shifting: '#f44336', // rouge
    luxury_shifting: '#e91e63', // rose
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'standard_non_shifting':
        return 'Standard non shifting';
      case 'standard_shifting':
        return 'Standard Shifting';
      case 'premium_non_shifting':
        return 'Premium non shifting';
      case 'premium_shifting':
        return 'Premium Shifting';
      case 'luxury_non_shifting':
        return 'Luxury non shifting';
      case 'luxury_shifting':
        return 'Luxury Shifting';
      default:
        return 'N/A';
    }
  };
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ mt: 2 }}
        >
          Retour
        </Button>
      </Box>
    );
  }

  if (!currentPackage) {
    return null;
  }

  return (
    <Box>
      {/* Fil d'Ariane */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
          >
            Accueil
          </Link>
          <Typography color="text.primary">{currentPackage.title}</Typography>
        </Breadcrumbs>
      </Box>

      {/* Entête avec titre et actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleGoBack} color="primary" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {currentPackage.title}
          </Typography>
        </Box>
        <IconButton onClick={handleShare} color="primary">
          <ShareIcon />
        </IconButton>
      </Box>

      {/* Informations sur la compagnie et la gamme */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar
          src={currentPackage.company.logo}
          alt={currentPackage.company.name}
          sx={{ width: 40, height: 40, mr: 2 }}
        />
        <Typography variant="h6" component="span" sx={{ mr: 3 }}>
          {currentPackage.company.name}
        </Typography>
        <Chip
          label={getCategoryLabel(currentPackage.category)}
          sx={{
            backgroundColor: categoryColors[currentPackage.category] || '#757575',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Colonne de gauche: Détails principaux */}
        <Grid item xs={12} md={8}>
          {/* Image principale */}
          <Card sx={{ mb: 3 }}>
            <CardMedia
              component="img"
              height="300"
              image={currentPackage.image || 'https://via.placeholder.com/800x300?text=Forfait+Hajj'}
              alt={currentPackage.title}
            />
          </Card>

          {/* Détails du forfait */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Description
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" paragraph>
              {currentPackage.description}
            </Typography>

            {/* Informations principales */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarMonthIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <strong>Durée:</strong> {currentPackage.days} jours
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EventAvailableIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <strong>Période:</strong> {new Date(currentPackage.startDate).toLocaleDateString()} - {new Date(currentPackage.endDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DollarIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <strong>Prix:</strong> {currentPackage.price.toLocaleString()} SAR
                  </Typography>
                </Box>
              </Grid>
              {currentPackage.availableSpots && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StarIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <strong>Places disponibles:</strong> {currentPackage.availableSpots}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Hébergement */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Hébergement
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={3}>
              {/* La Mecque */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Hôtel à La Mecque
                    </Typography>
                    
                    {currentPackage.hotelMecca ? (
                      <>
                        <List dense disablePadding>
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                              <HotelIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={currentPackage.hotelMecca.name}
                              secondary={`Zone ${currentPackage.hotelMecca.zone}`}
                            />
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                              <CalendarMonthIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Du ${new Date(currentPackage.hotelMecca.checkIn).toLocaleDateString()} au ${new Date(currentPackage.hotelMecca.checkOut).toLocaleDateString()}`}
                            />
                          </ListItem>
                        </List>
                        {currentPackage.hotelMecca.image && (
                          <CardMedia
                            component="img"
                            height="140"
                            image={currentPackage.hotelMecca.image}
                            alt={currentPackage.hotelMecca.name}
                            sx={{ mt: 2, borderRadius: 1 }}
                          />
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Informations sur l'hôtel à venir
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Médine */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Hôtel à Médine
                    </Typography>
                    
                    {currentPackage.hotelMedina ? (
                      <>
                        <List dense disablePadding>
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                              <HotelIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={currentPackage.hotelMedina.name}
                              secondary={`Zone ${currentPackage.hotelMedina.zone}`}
                            />
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                              <CalendarMonthIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Du ${new Date(currentPackage.hotelMedina.checkIn).toLocaleDateString()} au ${new Date(currentPackage.hotelMedina.checkOut).toLocaleDateString()}`}
                            />
                          </ListItem>
                        </List>
                        {currentPackage.hotelMedina.image && (
                          <CardMedia
                            component="img"
                            height="140"
                            image={currentPackage.hotelMedina.image}
                            alt={currentPackage.hotelMedina.name}
                            sx={{ mt: 2, borderRadius: 1 }}
                          />
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Informations sur l'hôtel à venir
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {/* Camp à Mina */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Camp à Mina
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {currentPackage.minaCamp ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    {currentPackage.minaCamp === 'AlMuaisim' ? 'Camp AlMuaisim' : 'Camp Alkabesh'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Informations sur le camp à venir
                </Typography>
              )}
            </Box>
          </Paper>
          
          {/* Vols */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Vols
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              {/* Vol aller */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Vol Aller
                    </Typography>
                    
                    {currentPackage.flights && currentPackage.flights.outbound ? (
                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemIcon sx={{ minWidth: 35 }}>
                            <FlightTakeoffIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`De ${currentPackage.departureCity}`}
                            secondary={new Date(currentPackage.flights.outbound.date).toLocaleDateString()}
                          />
                        </ListItem>
                        {currentPackage.flights.outbound.airline && (
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                              <StarIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={currentPackage.flights.outbound.airline}
                              secondary={currentPackage.flights.outbound.flightNumber}
                            />
                          </ListItem>
                        )}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Informations sur le vol à venir
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Vol retour */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Vol Retour
                    </Typography>
                    
                    {currentPackage.flights && currentPackage.flights.inbound ? (
                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemIcon sx={{ minWidth: 35 }}>
                            <FlightLandIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Vers ${currentPackage.departureCity}`}
                            secondary={new Date(currentPackage.flights.inbound.date).toLocaleDateString()}
                          />
                        </ListItem>
                        {currentPackage.flights.inbound.airline && (
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                              <StarIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={currentPackage.flights.inbound.airline}
                              secondary={currentPackage.flights.inbound.flightNumber}
                            />
                          </ListItem>
                        )}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Informations sur le vol à venir
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Colonne de droite: Résumé et contact */}
        <Grid item xs={12} md={4}>
          {/* Carte de prix et action */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h4" color="primary.main" gutterBottom>
              {currentPackage.price.toLocaleString()} SAR
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Durée:</strong> {currentPackage.days} jours
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Départ:</strong> {currentPackage.departureCity}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Date:</strong> {new Date(currentPackage.startDate).toLocaleDateString()}
              </Typography>
              {currentPackage.availableSpots && (
                <Typography variant="body1" gutterBottom>
                  <strong>Places disponibles:</strong> {currentPackage.availableSpots}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={() => window.location.href = `tel:${currentPackage.company.phone}`}
            >
              Contacter la compagnie
            </Button>
          </Paper>
          
          {/* Informations de contact */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contact
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Téléphone" 
                  secondary={
                    <Link 
                      href={`tel:${currentPackage.company.phone}`} 
                      color="inherit"
                    >
                      {currentPackage.company.phone}
                    </Link>
                  } 
                />
              </ListItem>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Email" 
                  secondary={
                    <Link 
                      href={`mailto:${currentPackage.company.email}`} 
                      color="inherit"
                    >
                      {currentPackage.company.email}
                    </Link>
                  } 
                />
              </ListItem>
              {currentPackage.company.website && (
                <ListItem disablePadding>
                  <ListItemIcon>
                    <LanguageIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Site web" 
                    secondary={
                      <Link 
                        href={currentPackage.company.website} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        color="inherit"
                      >
                        {currentPackage.company.website}
                      </Link>
                    } 
                  />
                </ListItem>
              )}
            </List>
          </Paper>
          
          {/* Détails de la compagnie */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              À propos de la compagnie
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={currentPackage.company.logo}
                alt={currentPackage.company.name}
                sx={{ width: 60, height: 60, mr: 2 }}
              />
              <Typography variant="h6">
                {currentPackage.company.name}
              </Typography>
            </Box>
            <Typography variant="body2" paragraph>
              {currentPackage.company.description || "Aucune description disponible."}
            </Typography>
            {currentPackage.company.licenseNumber && (
              <Typography variant="body2" color="text.secondary">
                N° Licence: {currentPackage.company.licenseNumber}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PackageDetailsPage;